import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context as ReactContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  IProvisioningSearchResult,
  IProvisioningStatusItem,
} from '../../Common/ProvisioningModels/ProvisioningStatus';

function formatDateUTC(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getFromDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 7);
  return formatDateUTC(d);
}

export function useProvisioningStatus(
  search: string,
  searchField: string
): [IProvisioningStatusItem[], boolean, boolean, () => void, () => void] {
  const [items, setItems] = useState<IProvisioningStatusItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const continuationTokenRef = useRef<string | null>(null);
  const { httpClient } = useContext(Context as ReactContext<IEmployeeExperienceContext>);

  function fetchData(continuationToken: string | null, append: boolean) {
    setLoading(true);
    let url = `${__API_BASE_URL__}/provisioning?pageSize=50`;

    const trimmed = search.trim();
    if (trimmed) {
      url += `&search=${encodeURIComponent(trimmed)}&searchField=${encodeURIComponent(searchField)}`;
    } else {
      url += `&fromDate=${getFromDate()}`;
    }

    if (continuationToken) {
      url += `&continuationToken=${encodeURIComponent(continuationToken)}`;
    }

    httpClient
      .request<IProvisioningSearchResult>({
        url,
        resource: __CLIENT_ID__,
      })
      .then(({ data }) => {
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setHasNextPage(data.hasNextPage);
        continuationTokenRef.current = data.continuationToken ?? null;
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    continuationTokenRef.current = null;
    setItems([]);
    fetchData(null, false);
  }, [search, searchField]);

  const loadMore = useCallback(() => {
    if (!hasNextPage || loading) return;
    fetchData(continuationTokenRef.current, true);
  }, [search, searchField, hasNextPage, loading]);

  const refresh = useCallback(() => {
    continuationTokenRef.current = null;
    setItems([]);
    fetchData(null, false);
  }, [search, searchField]);

  return [items, loading, hasNextPage, loadMore, refresh];
}
